import { Controller } from 'stimulus';

const LS_KEY = 'walkthroughPanel';

export default class extends Controller {
  static targets = [
    'toggleIcon',
    'defaultBody',
    'expandedBody',
    'step',
    'celebration',
    'progressFill',
  ];
  static values = {
    event: String,
    completedTaskKey: String,
    reloadUrl: String,
    percentage: Number,
  };

  connect() {
    if (!this.hasToggleIconTarget) {
      return;
    }

    const isTaskComplete = this.eventValue === 'task_completed';
    const isStepComplete = this.eventValue === 'step_completed';
    const completedTaskKey = this.completedTaskKeyValue;

    // Clear event values to prevent replay animations on clicking back in browser
    this.eventValue = '';
    this.completedTaskKeyValue = '';

    const shouldAutoExpand = isTaskComplete || isStepComplete;
    const storedState = this.#readStoredState();
    this.minimized = !shouldAutoExpand && storedState.minimized === true;
    this.expanded = shouldAutoExpand || storedState.expanded === true;

    this.element.classList.add('walkthrough-panel--no-anim');
    this.#updateExpandState();
    requestAnimationFrame(() => this.element.classList.remove('walkthrough-panel--no-anim'));

    if (typeof window.initTooltips === 'function') {
      window.initTooltips(this.element.querySelectorAll('.tooltip'));
    }

    if (isTaskComplete) {
      this.#playTaskCompleteAnimation(completedTaskKey);
    } else if (isStepComplete) {
      this.#animateProgressBar();
    }
  }

  toggleExpand(event) {
    if (event.target.closest('a')) {
      return;
    }

    const wasMinimized = this.minimized;

    if (this.minimized) {
      this.minimized = false;
    } else {
      this.expanded = !this.expanded;

      if (!this.expanded && this.hasDefaultBodyTarget) {
        this.defaultBodyTarget.hidden = true;
        setTimeout(() => (this.defaultBodyTarget.hidden = false), 250);
      }
    }

    this.#updateExpandState();
    this.#saveState();

    if (wasMinimized || this.expanded) {
      this.dispatch('expand');
    }
  }

  minimizeForTimeTracker(event) {
    if (
      !this.hasToggleIconTarget ||
      this.minimized ||
      !event.target.matches('.navigation--time-tracker')
    ) {
      return;
    }

    this.minimized = true;
    this.#updateExpandState();
  }

  minimize(event) {
    event.stopPropagation();

    this.minimized = true;
    this.#updateExpandState();
    this.#saveState();
  }

  toggleStep(event) {
    const stepIndex = parseInt(event.currentTarget.dataset.stepIndex, 10);
    const stepEl = this.stepTargets[stepIndex];
    if (!stepEl) {
      return;
    }

    const isOpen = stepEl.classList.contains('walkthrough-panel__step--open');
    this.stepTargets.forEach((el, i) => {
      if (i !== stepIndex) {
        el.classList.remove('walkthrough-panel__step--open');
      }
    });
    stepEl.classList.toggle('walkthrough-panel__step--open', !isOpen);
  }

  dismissCelebration() {
    if (!this.reloadUrlValue) {
      return;
    }

    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ minimized: false, expanded: true }));
    } catch (e) {
      // localStorage unavailable
    }

    fetch(this.reloadUrlValue, {
      headers: {
        Accept: 'text/vnd.turbo-stream.html, text/html',
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then((response) => response.text())
      .then((html) => {
        const frame = this.element.closest('turbo-frame');
        if (frame) {
          frame.outerHTML = html;
        }
      });
  }

  dismissPanel() {
    const frame = this.element.closest('turbo-frame');
    if (frame) {
      frame.remove();
    }
  }

  #playTaskCompleteAnimation(taskKey) {
    if (!taskKey) {
      return;
    }

    const taskEl = this.expandedBodyTarget.querySelector(`[data-task-key="${taskKey}"]`);
    if (!taskEl) {
      return;
    }

    const roundel = taskEl.querySelector('.walkthrough-panel__task-status');
    if (!roundel) {
      return;
    }

    taskEl.classList.add('walkthrough-panel__task--animating');

    setTimeout(() => {
      roundel.classList.add('walkthrough-panel__task-status--done');
      this.#burst(roundel);

      setTimeout(() => {
        taskEl.classList.add('walkthrough-panel__task--strike');

        setTimeout(() => {
          taskEl.classList.remove(
            'walkthrough-panel__task--active',
            'walkthrough-panel__task--animating',
            'walkthrough-panel__task--strike'
          );
          roundel.classList.remove('walkthrough-panel__task-status--done');
          taskEl.classList.add('walkthrough-panel__task--complete');

          this.#activateNextTask();
          this.#animateProgressBar();
        }, 800);
      }, 500);
    }, 400);
  }

  #animateProgressBar() {
    if (!this.hasProgressFillTarget) {
      return;
    }

    requestAnimationFrame(() => {
      this.progressFillTarget.style.width = `${this.percentageValue}%`;
    });
  }

  #activateNextTask() {
    const pending = this.expandedBodyTarget.querySelector(
      '.walkthrough-panel__task--pending-active'
    );
    if (!pending) {
      return;
    }

    pending.classList.remove('walkthrough-panel__task--pending-active');
    pending.classList.add('walkthrough-panel__task--active');
  }

  #burst(el) {
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const radius = rect.width / 2;
    const baseAngle = Math.random() * 360;
    const sizes = [...[9, 9, 6.5, 6.5, 4, 4]].sort(() => Math.random() - 0.5);

    for (let i = 0; i < 6; i++) {
      const angle = baseAngle + (360 / 6) * i + Math.random() * 30 - 15;
      const distance = radius + 15 + Math.random() * 25;
      const rad = (angle * Math.PI) / 180;
      const tx = Math.cos(rad) * distance;
      const ty = Math.sin(rad) * distance;
      const startX = cx + Math.cos(rad) * radius;
      const startY = cy + Math.sin(rad) * radius;
      const size = sizes[i];

      const dot = document.createElement('div');
      dot.className = 'walkthrough-particle';
      dot.style.cssText =
        `left:${startX - size / 2}px;` +
        `top:${startY - size / 2}px;` +
        `width:${size}px;` +
        `height:${size}px;` +
        `--tx:${tx}px;` +
        `--ty:${ty}px;`;
      document.body.appendChild(dot);
      dot.addEventListener('animationend', () => dot.remove());
    }
  }

  #updateExpandState() {
    const button = this.toggleIconTarget;
    const label = this.minimized || !this.expanded ? 'Expand' : 'Collapse';
    const tooltipContent = button.querySelector('.tooltip__content');

    if (tooltipContent) {
      tooltipContent.textContent = label;
    }

    if (button._tippy) {
      button._tippy.setContent(label);
    } else if (typeof window.initTooltips === 'function') {
      window.initTooltips([button]);
    }

    this.element.classList.toggle('walkthrough-panel--minimized', this.minimized);
    this.element.classList.toggle('walkthrough-panel--expanded', this.expanded && !this.minimized);
  }

  #readStoredState() {
    try {
      const storedPanelState = localStorage.getItem(LS_KEY);
      if (!storedPanelState) {
        return {};
      }
      if (storedPanelState === 'expanded') {
        return { expanded: true };
      }
      if (storedPanelState === 'collapsed') {
        return {};
      }
      const parsed = JSON.parse(storedPanelState);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  #saveState() {
    try {
      localStorage.setItem(
        LS_KEY,
        JSON.stringify({ minimized: this.minimized, expanded: this.expanded })
      );
    } catch (e) {
      // localStorage unavailable
    }
  }
}
